import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import AdminGuard from "@/components/AdminGuard";

import Home from "@/pages/Home";
import Chat from "@/pages/Chat";
import Verify from "@/pages/Verify";
import PriceCheck from "@/pages/PriceCheck";
import StyleMatch from "@/pages/StyleMatch";
import Resell from "@/pages/Resell";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Pricing from "@/pages/Pricing";
import Press from "@/pages/Press";
import LegalPage from "@/components/LegalPage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminBoardRates from "@/pages/admin/AdminBoardRates";
import AdminPosts from "@/pages/admin/AdminPosts";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminKnowledge from "@/pages/admin/AdminKnowledge";
import AdminAlerts from "@/pages/admin/AdminAlerts";
import AlertsUnsubscribe from "@/pages/AlertsUnsubscribe";
import WhatsAppFloating from "@/components/WhatsAppFloating";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";

const PublicRoutes = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/price-check" element={<PriceCheck />} />
      <Route path="/style-match" element={<StyleMatch />} />
      <Route path="/resell" element={<Resell />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/press" element={<Press />}/>
      <Route path="/privacy" element={<LegalPage slug="privacy" />} />
      <Route path="/terms" element={<LegalPage slug="terms" />} />
      <Route path="/ai-policy" element={<LegalPage slug="ai-policy" />} />
      <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/alerts/unsubscribe" element={<AlertsUnsubscribe />} />
      <Route path="/dashboard" element={<Dashboard />} />
    </Routes>
  </Layout>
);

const AdminRoutes = () => (
  <AdminGuard>
    <Routes>
      <Route path="/" element={<AdminOverview />} />
      <Route path="/board-rates" element={<AdminBoardRates />} />
      <Route path="/posts" element={<AdminPosts />} />
      <Route path="/audit-logs" element={<AdminAuditLogs />} />
      <Route path="/legal" element={<AdminLegal />} />
      <Route path="/documents" element={<AdminDocuments />} />
      <Route path="/quotes" element={<AdminQuotes />} />
      <Route path="/users" element={<AdminUsers />} />
      <Route path="/knowledge" element={<AdminKnowledge />} />
      <Route path="/alerts" element={<AdminAlerts />} />
      <Route path="/settings" element={<AdminSettings />} />
    </Routes>
  </AdminGuard>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const tick = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/admin/*" element={<AdminRoutes />} />
            <Route path="/*" element={<PublicRoutes />} />
          </Routes>
          <Toaster position="top-center" richColors />
          <WhatsAppFloating />
          <PwaInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
